import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Database, FileText, Plus } from "lucide-react";
import type { KnowledgeBase } from "@/services/knowledge-base.service";
import { fileCount } from "@/utils/kb-upload-rules";

interface KbFolderSelectorDialogProps {
  stores: KnowledgeBase[];
  selectedStoreIds: string[];
  onSelectionChanged: (selectedIds: string[]) => void;
}

/** The agent editor's picker: which of the workspace's knowledge bases the agent may search. */
export function KbFolderSelectorDialog({
  stores,
  selectedStoreIds,
  onSelectionChanged,
}: KbFolderSelectorDialogProps) {
  const [open, setOpen] = useState(false);
  const [localSelection, setLocalSelection] = useState<string[]>(selectedStoreIds);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      // Reset local selection to current selection when opening
      setLocalSelection(selectedStoreIds);
    }
  };

  const toggleStore = (storeId: string) => {
    setLocalSelection((prev) =>
      prev.includes(storeId)
        ? prev.filter((id) => id !== storeId)
        : [...prev, storeId]
    );
  };

  const handleSave = () => {
    onSelectionChanged(localSelection);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Plus className="w-4 h-4" />
          Assign Knowledge Base
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            Select Knowledge Bases
          </DialogTitle>
          <DialogDescription>
            Choose which knowledge bases this agent can access for knowledge retrieval.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[300px] pr-4">
          <div className="space-y-2">
            {stores.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No knowledge bases available.</p>
                <p className="text-xs mt-1">
                  Create a knowledge base in the Knowledge Base page first.
                </p>
              </div>
            ) : (
              stores.map((store) => {
                const isSelected = localSelection.includes(store.id);
                return (
                  <div
                    key={store.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => toggleStore(store.id)}
                  >
                    <Checkbox
                      checked={isSelected}
                      aria-label={store.display_name}
                      className="pointer-events-none"
                    />
                    <div className="flex items-center justify-center w-8 h-8 rounded bg-primary/10">
                      <Database className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {store.display_name}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <FileText className="w-3 h-3" />
                        <span>{fileCount(store.file_count)}</span>
                        {store.tool_description && (
                          <Badge variant="outline" className="text-xs py-0">
                            Has description
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={stores.length === 0}>
            Save Selection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
